import { useDeepCompareMemo } from '@react-hookz/web';
import { useCallback } from 'react';

import { useSession } from '~hooks/useSession';
import { convertToBackendId } from '~utils/ids';

import { sendEvent } from './sendEvent';

const cb = <Extra extends { [key: string]: unknown }>(
	userId: string,
	isDemoUser: boolean,
	event: string,
	extra?: Extra,
	params?: { [key: string]: unknown },
	options?: {
		forceSendNoUser?: true;
		forceSendDemoUser?: true;
		includeDemoUserMarker?: boolean;
	}
) => {
	if (!userId) return;
	const bot_UserId = params?.bot_UserId ?? extra?.bot_UserId ?? null;
	const botId = bot_UserId
		? { bot_UserId: convertToBackendId(bot_UserId as string) }
		: {};
	const zoneParams = options?.includeDemoUserMarker
		? {
				zone: isDemoUser ? 'non-auth' : 'auth',
		  }
		: {};
	const body = {
		...params,
		...extra,
		...botId,
		...zoneParams,
		timestamp: (Date.now() / 1000) | 0,
	};

	if (!isDemoUser || options?.forceSendNoUser || options?.forceSendDemoUser) {
		void sendEvent({
			userId,
			event,
			body,
			forceSendNoUser: options?.forceSendNoUser,
			forceSendDemoUser: options?.forceSendDemoUser,
		});
	}
};

export const useSendDynamicEvent = <Extra extends { [key: string]: unknown }>(
	params?: { [key: string]: unknown },
	options?: {
		forceSendNoUser?: true;
		forceSendDemoUser?: true;
		includeDemoUserMarker?: boolean;
	}
): ((event: string, extra?: Extra) => void) => {
	const { userId, isDemoUser } = useSession();

	return useCallback(
		(event, extra) => cb(userId, isDemoUser, event, extra, params, options),
		[userId, isDemoUser, params, options]
	);
};

export const useSendEvent = <Extra extends { [key: string]: unknown }>(
	event: string,
	params?: { [key: string]: unknown },
	options?: {
		forceSendNoUser?: true;
		forceSendDemoUser?: true;
		includeDemoUserMarker?: boolean;
	}
): ((extra?: Extra) => void) => {
	const { userId, isDemoUser } = useSession();
	const memoizedOption = useDeepCompareMemo(() => options, [options]);
	const memoizedParams = useDeepCompareMemo(() => params, [params]);

	return useCallback(
		(extra) => {
			return cb(
				userId,
				isDemoUser,
				event,
				extra,
				memoizedParams,
				memoizedOption
			);
		},
		[userId, isDemoUser, event, memoizedParams, memoizedOption]
	);
};
